// Password-reset confirm page — self-serve (2026-07-20).
//
// The reset email links here: /auth/password-reset/confirm?token=RAW. We show
// password + confirm fields (min 12 chars) and POST /auth/password-reset/confirm.
// Unlike the invite flow, the API deliberately does NOT auto-login here — so on
// success we show a "sign in with your new password" state that links to /login.
// A 404 (expired/used token) shows a friendly state, also linking to /login
// where the Forgot-password flow lives.
//
// Editorial layout mirrors LoginPage / SetPasswordPage: split brand panel,
// same Input/Button primitives + brand palette.
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FlowLockup } from "@/components/FlowLockup";
import { ApiError, api } from "@/lib/api";

const MIN_PASSWORD = 12;

function BrandPanel() {
  return (
    <div className="relative hidden bg-brown-500 p-12 text-cream-200 md:flex md:flex-col md:justify-between">
      <FlowLockup width={112} className="self-start text-white" />
      <div>
        <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight text-cream-200">
          Reset your
          <br />
          password.
        </h1>
        <p className="mt-6 max-w-md text-base text-cream-300/80">
          Choose a new password and you'll be back in your other
          workspace in seconds.
        </p>
      </div>
      <div className="text-xs text-cream-200/60">
        © {new Date().getFullYear()} other · Boca Raton, FL
      </div>
    </div>
  );
}

export function ResetPasswordConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"form" | "done" | "expired">(
    token ? "form" : "expired",
  );

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    password.length >= MIN_PASSWORD && confirm === password && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api("/auth/password-reset/confirm", {
        method: "POST",
        body: JSON.stringify({ token, password }),
        skipAuth: true,
      });
      // API intentionally does not auto-login — send them to /login.
      setOutcome("done");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setOutcome("expired");
        } else if (err.status === 400) {
          setError(`Password must be at least ${MIN_PASSWORD} characters.`);
        } else {
          setError("Something went wrong. Please try again in a moment.");
        }
      } else {
        setError("Network error. Check your connection and try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <BrandPanel />

      <div className="flex items-center justify-center bg-cream-50 px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 md:hidden">
            <FlowLockup width={96} className="text-ink" />
          </div>

          {outcome === "done" && (
            <div>
              <h2 className="font-display text-3xl font-bold text-brown-500">
                Password updated
              </h2>
              <p className="mt-3 text-sm text-brown-400">
                Password updated — sign in with your new password.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block rounded-md bg-ink px-6 py-3 text-base font-semibold text-white hover:bg-ink-700"
              >
                Go to sign in
              </Link>
            </div>
          )}

          {outcome === "expired" && (
            <div>
              <h2 className="font-display text-3xl font-bold text-brown-500">
                This link isn't active
              </h2>
              <p className="mt-3 text-sm text-brown-400">
                This password-reset link has expired or was already used.
                Head to sign in and use "Forgot password?" to get a fresh
                one.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-sm font-semibold text-brown-400 underline-offset-2 hover:text-brown-500 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          )}

          {outcome === "form" && (
            <>
              <h2 className="font-display text-3xl font-bold text-brown-500">
                Choose a new password
              </h2>
              <p className="mt-2 text-sm text-brown-400">
                Pick something at least {MIN_PASSWORD} characters long.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                <div>
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-sm font-semibold text-brown-500"
                  >
                    New password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 12 characters"
                  />
                  {tooShort && (
                    <p className="mt-1.5 text-xs text-rust-600">
                      Must be at least {MIN_PASSWORD} characters.
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="confirm"
                    className="mb-1.5 block text-sm font-semibold text-brown-500"
                  >
                    Confirm password
                  </label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                  {mismatch && (
                    <p className="mt-1.5 text-xs text-rust-600">
                      Passwords don't match.
                    </p>
                  )}
                </div>

                {error && (
                  <div
                    role="alert"
                    className="rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="destructive"
                  size="lg"
                  className="w-full"
                  disabled={!canSubmit}
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
